/**
 * Thin localStorage wrapper that replaces `VenueOS.storage` and
 * `VenueOS.storage.local` from the App Builder runtime.
 *
 * Both the sync (`storage.local`) and async (`storage`) APIs used in the
 * original app map to the same browser `localStorage` here. The async
 * surface returns promises so we don't need to rewrite every call site.
 */

const PREFIX = 'venue-overview:';

function key(k: string): string {
  return PREFIX + k;
}

function readRaw(k: string): string | null {
  try {
    return window.localStorage.getItem(key(k));
  } catch {
    return null;
  }
}

function writeRaw(k: string, value: string): void {
  try {
    window.localStorage.setItem(key(k), value);
  } catch {
    // Quota exceeded or storage disabled — silently ignore to match
    // App Builder behavior.
  }
}

function deleteRaw(k: string): void {
  try {
    window.localStorage.removeItem(key(k));
  } catch {
    // Ignore.
  }
}

/**
 * Async, JSON-encoded storage (App Builder's `VenueOS.storage`). Values
 * are serialized with `JSON.stringify` on set, parsed on get. Strings
 * that aren't valid JSON are returned verbatim to match the App Builder
 * runtime's permissive behavior.
 */
export const storage = {
  async get<T = unknown>(k: string): Promise<T | null> {
    const raw = readRaw(k);
    if (raw == null) return null;
    try {
      return JSON.parse(raw) as T;
    } catch {
      return raw as unknown as T;
    }
  },

  async set(k: string, value: unknown): Promise<void> {
    const encoded = typeof value === 'string' ? value : JSON.stringify(value);
    writeRaw(k, encoded);
  },

  async delete(k: string): Promise<void> {
    deleteRaw(k);
  },

  /**
   * Sync-ish local area (App Builder's `VenueOS.storage.local`). The
   * original returns a Promise, so we keep the same async signature.
   */
  local: {
    async get<T = unknown>(k: string): Promise<T | null> {
      const raw = readRaw(k);
      if (raw == null) return null;
      try {
        return JSON.parse(raw) as T;
      } catch {
        return raw as unknown as T;
      }
    },

    async set(k: string, value: unknown): Promise<void> {
      const encoded = typeof value === 'string' ? value : JSON.stringify(value);
      writeRaw(k, encoded);
    },

    async delete(k: string): Promise<void> {
      deleteRaw(k);
    },
  },
};
